import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldLogo } from "../components/landing/Logo";
import { 
  DollarSign, Search, Edit3, Save, X, 
  TrendingUp, TrendingDown, RefreshCw, Home, ChefHat,
  Package, Beef, Milk, Leaf, Wine, Sparkles, Bell, BellOff, LogOut
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icons and colors
const CATEGORY_CONFIG = {
  Protein: { icon: Beef, color: "text-red-400", bg: "bg-red-400/10" },
  Produce: { icon: Leaf, color: "text-green-400", bg: "bg-green-400/10" },
  Dairy: { icon: Milk, color: "text-blue-400", bg: "bg-blue-400/10" },
  Pantry: { icon: Package, color: "text-yellow-400", bg: "bg-yellow-400/10" },
  Bar: { icon: Wine, color: "text-purple-400", bg: "bg-purple-400/10" },
  Cleaning: { icon: Sparkles, color: "text-cyan-400", bg: "bg-cyan-400/10" },
};

// Price Editor Row Component
const PriceRow = ({ item, onUpdate, isUpdating }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editPrice, setEditPrice] = useState(item.price);
  const category = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.Pantry;
  const CategoryIcon = category.icon;

  const handleSave = async () => {
    if (editPrice === item.price) {
      setIsEditing(false);
      return;
    }
    await onUpdate(item.id, editPrice);
    setIsEditing(false);
  };

  const priceChange = editPrice - item.price;
  const priceChangePercent = item.price > 0 ? ((priceChange / item.price) * 100).toFixed(1) : 0;

  return (
    <motion.tr 
      className="border-b border-indigo-500/20 hover:bg-indigo-700/10 transition-colors"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg ${category.bg} flex items-center justify-center`}>
            <CategoryIcon className={`w-4 h-4 ${category.color}`} />
          </div>
          <div>
            <p className="text-sm text-white font-medium">{item.item}</p>
            <p className="text-xs text-slate-500">{item.keywords?.slice(0, 3).join(", ")}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-1 rounded ${category.bg} ${category.color}`}>
          {item.category}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-slate-400 font-mono">{item.unit}</td>
      <td className="px-4 py-3">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <span className="text-slate-400">$</span>
            <Input
              type="number"
              step="0.01"
              value={editPrice}
              onChange={(e) => setEditPrice(parseFloat(e.target.value) || 0)}
              className="w-24 h-8 bg-indigo-700/50 border-gold/30 text-white text-sm"
              autoFocus
              data-testid={`price-input-${item.id}`}
            />
            {priceChange !== 0 && (
              <span className={`text-xs flex items-center gap-1 ${priceChange > 0 ? "text-red-400" : "text-lime"}`}>
                {priceChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {priceChangePercent}%
              </span>
            )}
          </div>
        ) : (
          <span className="text-lg font-bold text-gold">${item.price.toFixed(2)}</span>
        )}
      </td>
      <td className="px-4 py-3">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <Button
              onClick={handleSave}
              disabled={isUpdating}
              size="sm"
              className="h-7 bg-lime hover:bg-lime/80 text-indigo-700"
              data-testid={`save-price-${item.id}`}
            >
              <Save className="w-3 h-3" />
            </Button>
            <Button
              onClick={() => { setIsEditing(false); setEditPrice(item.price); }}
              size="sm"
              variant="ghost"
              className="h-7 text-slate-400"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => setIsEditing(true)}
            size="sm"
            variant="ghost"
            className="h-7 text-slate-400 hover:text-gold"
            data-testid={`edit-price-${item.id}`}
          >
            <Edit3 className="w-3 h-3" />
          </Button>
        )}
      </td>
    </motion.tr>
  );
};

// Affected SOPs Modal
const AffectedSOPsModal = ({ isOpen, onClose, sops, ingredientName, priceChange }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-[#040F49] border border-gold/20 rounded-xl w-full max-w-lg max-h-[70vh] flex flex-col overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-4 border-b border-gold/15 bg-[#060D3A]">
            <h3 className="font-inter text-sm font-bold text-gold">SOPs Affected by Price Change</h3>
            <p className="text-xs text-slate-400 mt-1">
              {ingredientName} price changed by {priceChange > 0 ? "+" : ""}{priceChange.toFixed(2)}
            </p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {sops.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No SOPs use this ingredient</p>
            ) : (
              sops.map((sop, i) => (
                <div key={i} className="bg-indigo-700/20 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white font-medium">{sop.title}</p>
                    <p className="text-xs text-slate-400">{sop.department?.toUpperCase()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">New Cost</p>
                    <p className="text-sm font-bold text-gold">${sop.new_cost?.toFixed(2)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="p-4 border-t border-gold/15">
            <Button onClick={onClose} className="w-full bg-gold hover:bg-gold/80 text-indigo-700">
              Got It
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Main Admin Inventory Page
export default function AdminInventory() {
  const { logout } = useAuth();
  const [prices, setPrices] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showAffectedModal, setShowAffectedModal] = useState(false);
  const [affectedSOPs, setAffectedSOPs] = useState([]);
  const [lastUpdateInfo, setLastUpdateInfo] = useState(null);
  const [alertThreshold, setAlertThreshold] = useState(35);
  const [alertsEnabled, setAlertsEnabled] = useState(false);

  const fetchPrices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ingredient-prices`);
      setPrices(res.data.prices || []);
      setMetadata(res.data.metadata);
    } catch (err) {
      toast.error("Failed to load prices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  const updatePrice = async (id, newPrice) => {
    setUpdating(true);
    try {
      const res = await axios.put(`${API}/ingredient-prices/${id}`, { price: newPrice });
      
      // Update local state
      setPrices(prev => prev.map(p => p.id === id ? { ...p, price: newPrice } : p));
      
      // Show affected SOPs
      if (res.data.affected_sops?.length > 0) {
        const item = prices.find(p => p.id === id);
        setLastUpdateInfo({
          ingredientName: item?.item,
          priceChange: newPrice - (item?.price || 0)
        });
        setAffectedSOPs(res.data.affected_sops);
        setShowAffectedModal(true);
      }
      
      toast.success("Price updated! SOPs recalculated.");
    } catch (err) {
      toast.error("Failed to update price");
    } finally {
      setUpdating(false);
    }
  };

  // Filter prices
  const filteredPrices = prices.filter(p => {
    const matchesSearch = p.item.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.keywords?.some(k => k.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === "all" || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories
  const categories = [...new Set(prices.map(p => p.category))].filter(Boolean);

  // Calculate stats
  const totalItems = prices.length;
  const avgPrice = prices.length > 0 ? prices.reduce((sum, p) => sum + p.price, 0) / prices.length : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#040F49] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#040F49]" data-testid="admin-inventory-page">
      <div className="noise-overlay" />
      
      {/* Header */}
      <div className="sticky top-0 z-50 backdrop-blur-xl bg-indigo-700/90 border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldLogo size="sm" />
            <span className="font-inter text-sm font-bold text-gold tracking-wider">ADMIN COMMAND CENTER</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/dashboard/sop-builder" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm">
              <ChefHat className="w-4 h-4" /> SOP Builder
            </a>
            <a href="/admin/founding-50" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm">
              <DollarSign className="w-4 h-4" /> Leads
            </a>
            <Button onClick={logout} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card rounded-xl p-5 border border-gold/10">
            <span className="text-xs text-slate-400">Total Ingredients</span>
            <p className="text-2xl font-inter font-black text-white mt-1">{totalItems}</p>
          </div>
          <div className="glass-card rounded-xl p-5 border border-gold/10">
            <span className="text-xs text-slate-400">Avg Price/Unit</span>
            <p className="text-2xl font-inter font-black text-gold mt-1">${avgPrice.toFixed(2)}</p>
          </div>
          <div className="glass-card rounded-xl p-5 border border-gold/10">
            <span className="text-xs text-slate-400">Categories</span>
            <p className="text-2xl font-inter font-black text-white mt-1">{categories.length}</p>
          </div>
          <div className="glass-card rounded-xl p-5 border border-gold/10">
            <span className="text-xs text-slate-400">Last Updated</span>
            <p className="text-sm font-inter text-white mt-1">{metadata?.last_updated?.slice(0, 10) || "N/A"}</p>
          </div>
        </div>

        {/* Alert Threshold Setting */}
        <div className="glass-card rounded-xl p-4 border border-gold/10 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alertsEnabled ? "bg-lime/20" : "bg-slate-600/20"}`}>
                {alertsEnabled ? <Bell className="w-5 h-5 text-lime" /> : <BellOff className="w-5 h-5 text-slate-500" />}
              </div>
              <div>
                <p className="text-sm font-medium text-white">Cost Threshold Alerts</p>
                <p className="text-xs text-slate-400">Notify when SOP food cost exceeds target</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-400">Threshold:</Label>
                <Input
                  type="number"
                  value={alertThreshold}
                  onChange={(e) => setAlertThreshold(parseInt(e.target.value) || 30)}
                  className="w-16 h-8 bg-indigo-700/50 border-gold/30 text-white text-sm text-center"
                  data-testid="alert-threshold"
                />
                <span className="text-xs text-slate-400">%</span>
              </div>
              <Button
                onClick={() => {
                  setAlertsEnabled(!alertsEnabled);
                  toast.success(alertsEnabled ? "Alerts disabled" : `Alerts enabled at ${alertThreshold}%`);
                }}
                variant={alertsEnabled ? "default" : "outline"}
                size="sm"
                className={alertsEnabled ? "bg-lime text-indigo-700" : "border-slate-500 text-slate-400"}
                data-testid="toggle-alerts"
              >
                {alertsEnabled ? "Enabled" : "Enable"}
              </Button>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search ingredients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-indigo-700/30 border-gold/20 text-white"
              data-testid="search-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48 bg-indigo-700/30 border-gold/20 text-white" data-testid="category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-indigo-700 border-gold/20">
              <SelectItem value="all" className="text-white">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Price Table */}
        <div className="glass-card rounded-xl border border-gold/10 overflow-hidden">
          <div className="p-4 border-b border-gold/10 flex items-center justify-between">
            <h3 className="font-inter text-sm font-bold text-gold tracking-wider uppercase">
              Ingredient Price List
            </h3>
            <span className="text-xs text-slate-400">{filteredPrices.length} items</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-indigo-500/30 text-xs text-slate-400 font-mono">
                  <th className="px-4 py-3 text-left">Ingredient</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Unit</th>
                  <th className="px-4 py-3 text-left">Price</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrices.map(item => (
                  <PriceRow
                    key={item.id}
                    item={item}
                    onUpdate={updatePrice}
                    isUpdating={updating}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Link */}
        <div className="text-center mt-8">
          <p className="text-xs text-slate-500">
            Prices sourced from: {metadata?.source || "Founding-50-Wholesale-Average"}
          </p>
        </div>
      </div>

      {/* Affected SOPs Modal */}
      <AffectedSOPsModal
        isOpen={showAffectedModal}
        onClose={() => setShowAffectedModal(false)}
        sops={affectedSOPs}
        ingredientName={lastUpdateInfo?.ingredientName}
        priceChange={lastUpdateInfo?.priceChange || 0}
      />
    </div>
  );
}
